import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Users, Zap, Star } from 'lucide-react';
import './CallToAction.css';

const features = [
  { icon: Users, label: '100+ Active Members' },
  { icon: Zap, label: '30+ Events Annually' },
  { icon: Star, label: 'Industry Partnerships' },
];

const CallToAction = () => {
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const navigateToCareers = () => {
    window.location.href = '/careers';
  };

  return (
    <section className="cta-section">
      {/* Ambient background glow */}
      <div className="cta-bg-glow" aria-hidden="true" />

      {/* Decorative floating icons – hidden on mobile via CSS */}
      <div className="cta-floaters" aria-hidden="true">
        {[
          { Icon: Users, anim: { y: [0, -15, 0], rotate: [0, 5, 0] }, dur: 4 },
          { Icon: Zap, anim: { y: [0, 10, 0], rotate: [0, -5, 0] }, dur: 5 },
          { Icon: Star, anim: { y: [0, -8, 0], rotate: [0, 3, 0] }, dur: 6 },
        ].map(({ Icon, anim, dur }, i) => (
          <motion.div
            key={i}
            className="cta-floater"
            animate={anim}
            transition={{ duration: dur, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Icon size={24} />
          </motion.div>
        ))}
      </div>

      <div className="cta-wrapper">

        {/* ── Heading ── */}
        <motion.h2
          className="cta-heading"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
        >
          Transforming Ideas into Impact —{' '}
          <span className="cta-heading-accent">AIEC IITM</span>
        </motion.h2>

        {/* ── Description ── */}
        <motion.p
          className="cta-body"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          viewport={{ once: true }}
        >
          AIEC IITM is a launchpad for innovators, creators, and changemakers —
          empowering students to collaborate, experiment, and turn ideas into
          real-world impact.
        </motion.p>

        {/* ── Tagline ── */}
        <motion.p
          className="cta-tagline"
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
          viewport={{ once: true }}
        >
          Learn. Build. Lead.
        </motion.p>

        {/* ── Stat pills ── */}
        <motion.div
          className="cta-features"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.35 }}
          viewport={{ once: true }}
        >
          {features.map(({ icon: Icon, label }) => (
            <div className="cta-feature" key={label}>
              <div className="cta-feature-icon">
                <Icon size={18} />
              </div>
              <span>{label}</span>
            </div>
          ))}
        </motion.div>

        {/* ── Buttons ── */}
        <motion.div
          className="cta-actions"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.45 }}
          viewport={{ once: true }}
        >
          <motion.button
            className="cta-btn cta-btn--primary"
            whileHover={{ scale: 1.04, y: -2 }}
            whileTap={{ scale: 0.96 }}
            onClick={navigateToCareers}
          >
            Become a Member <ArrowRight size={18} />
          </motion.button>

          <motion.button
            className="cta-btn cta-btn--secondary"
            whileHover={{ scale: 1.04, y: -2 }}
            whileTap={{ scale: 0.96 }}
            onClick={() => scrollToSection('about')}
          >
            Learn More
          </motion.button>
        </motion.div>

      </div>
    </section>
  );
};

export default CallToAction;
